<template lang="pug">
.flex-column
  h1.mb-4 {{ $t("myData") }}
  .flex.justify-center.mb-4
    smx-form(v-loading="myAccountStore.userDataLoadingState.loading")
      smx-form-item(:label="$t('username')")
        span {{ editableData.username }}
      smx-form-item(:label="$t('firstName')")
        el-input(v-model="editableData.firstName")
      smx-form-item(:label="$t('lastName')")
        el-input(v-model="editableData.lastName")
      smx-form-item(:label="$t('email')")
        el-input(v-model="editableData.email")
      smx-form-item(:label="$t('language')")
        LanguageSelect(v-model="language")

  .flex.justify-end
    el-button(type="danger", @click="close") {{ $t("close") }}
    el-button(v-loading="saveLoading", type="primary", @click="saveChanges") {{ $t("save") }}
</template>

<script lang="ts" setup>
import { onMounted, ref, computed, watch } from "vue";
import LanguageSelect from "./components/LanguageSelect.vue";
import { useMyAccountStore } from "../../stores/myAccount";
import { ElNotification } from "element-plus";
import { useI18n } from "vue-i18n";
import { showResponseErrorToUser } from "@shared/util/genericResponseErrors";

type EditableUserData = {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  attributes: {
    language: string[];
  };
};

const editableData = ref<EditableUserData>({
  username: "",
  firstName: "",
  lastName: "",
  email: "",
  attributes: {
    language: [],
  },
});

const myAccountStore = useMyAccountStore();

const userData = computed(() => myAccountStore.userData);

function setData() {
  if (!userData.value) {
    return;
  }

  editableData.value = {
    username: userData.value.username,
    firstName: userData.value.firstName,
    lastName: userData.value.lastName,
    email: userData.value.email,
    attributes: {
      language: [...userData.value.attributes.language],
    },
  };
}

watch(userData, setData, { immediate: true });

const language = computed<string>({
  get() {
    return editableData.value.attributes.language[0] ?? "";
  },
  set(val) {
    editableData.value.attributes.language = [val];
  },
});

async function loadData() {
  await myAccountStore.initUserData(false);
}

const emit = defineEmits(["close"]);

function close() {
  emit("close");
}

const saveLoading = ref(false);

const { t } = useI18n();

async function saveChanges() {
  saveLoading.value = true;
  try {
    await myAccountStore.updateUserData(editableData.value);
    const selectedLanguage = editableData.value.attributes.language[0];
    if (selectedLanguage) {
      myAccountStore.setLanguage(selectedLanguage);
    }
    ElNotification({
      title: t("changesSaved"),
      type: "success",
    });
  } catch (e) {
    showResponseErrorToUser(e, t);
  } finally {
    saveLoading.value = false;
  }
}

onMounted(() => {
  void loadData();
});
</script>
